import { useState } from "react";
import {
  useGetList,
  useNotify,
  useRecordContext,
  type Identifier,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

import { RelativeDate } from "../misc/RelativeDate";
import type { Contact } from "../types";
import { MARKETING_PATHS, lifecyclePath, sequencePath } from "./paths";
import { trackLabel } from "./sequenceRules";
import {
  activeEnrollment,
  canEnroll,
  enrollViaSupabase,
  enrollableSequences,
  type EnrollRpc,
  progress,
  startEnrollment,
  suspension,
} from "./enrollment";
import { todayLocal } from "./touchActions";
import { formatDay, headline, invalidateLifecycle } from "./lifecycleRules";
import type {
  ContactHealth,
  Enrollment,
  EnrollmentStep,
  Sequence,
  SequenceStep,
} from "./types";

/**
 * ══════════════════════════════════════════════════════════════════════════
 * A SUMMARY, AND THE ONE DECISION THAT BELONGS ON A RECORD.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * WHAT THIS PANEL USED TO BE, AND WHY IT IS NOT THAT ANY MORE
 * -----------------------------------------------------------
 * It carried the track, the stage, the cadence override, the pause, the
 * enrollment and its progress -- everything the lifecycle has -- squeezed
 * above a contact's notes feed. The result was measured directly: a contact
 * was enrolled, the panel said "0 of 5 steps done, and step 1 is next", and
 * the person who did it concluded there was "basically nothing in the UI I
 * could do anything with… no idea it's even doing anything".
 *
 * They were right about the panel and wrong about the system. The plan was
 * there -- five dated steps, one of them already a task -- and a progress
 * counter is not a plan. So the plan, and everything that decides it, moved to
 * a page with room for it: LifecycleScreen.tsx at /contacts/:id/lifecycle.
 *
 * WHAT STAYS HERE, AND WHY EXACTLY THESE
 * ---------------------------------------
 *   - WHERE THEY ARE and WHEN THEY ARE DUE. One line each. This is a record
 *     you open to remind yourself who somebody is, and both belong in that
 *     glance.
 *   - WHETHER A CADENCE IS RUNNING, and what it does next. The failure this
 *     whole system was built to end is a person going quiet while their record
 *     looks fine, so "nothing is running on this contact" has to be legible
 *     from the record itself.
 *   - THE DECISION TO START SOMEBODY. Enrolling is something you do to a
 *     PERSON, having just read their record -- which is where you are. It is
 *     the one control here that is not a summary, and it is here for the same
 *     reason it was always here.
 *
 * Everything else -- retiming, skipping, adding, reordering, the stage, the
 * cadence, the pause -- needs the plan on screen beside it to be safe, and is
 * one link away.
 *
 * WHY IT READS `contact_health` AND NOT THE CONTACT RECORD
 * --------------------------------------------------------
 * The lifecycle columns are NOT on the contact page's record. The data
 * provider rewrites getOne("contacts") to `contacts_summary`, an upstream view
 * that lists its columns explicitly and knows nothing about ours -- and 065
 * removed the columns from `contacts` entirely, so there is nothing there to
 * widen the view with. Reading anything else here would be reading a second
 * opinion.
 */
export const LifecyclePanel = ({
  /**
   * enroll_contact is a security-definer RPC that writes the enrollment AND
   * its whole plan in one transaction, so it goes through the Supabase client
   * rather than the data provider. Injected for the reason OutboundQueue takes
   * its `cancel`: the write being tested is the one whose failure mode is a
   * contact silently never nurtured, and it should be provable without a
   * credential or a network. See ./enrollment.ts.
   */
  enroll = enrollViaSupabase,
}: { enroll?: EnrollRpc } = {}) => {
  const contact = useRecordContext<Contact>();
  const contactId = contact?.id;

  const today = todayLocal();

  const {
    data: healthRows,
    isPending: healthPending,
    error: healthError,
  } = useGetList<ContactHealth>(
    "contact_health",
    {
      filter: { id: contactId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: contactId != null },
  );

  const health = healthRows?.[0] ?? null;

  if (!contact) return null;

  const state = headline(health, today);

  return (
    <div className="mt-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">Lifecycle</h3>
        <span className="text-xs text-muted-foreground">
          {/*
            Silent while loading. "Not overdue" is a claim, and a claim must
            not be made from an answer we do not have yet.
          */}
          {healthPending ? null : <Headline state={state} />}
        </span>
      </div>
      <Separator className="mt-1.5" />

      {healthError ? (
        <p className="mt-3 text-sm text-destructive">
          Could not read this contact&apos;s follow-up state, so this is not a
          record of them being up to date — it is a record of not knowing.
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          <p className="text-sm">
            {health?.lifecycle_stage && health.track ? (
              <>
                <strong>
                  {health.track} · {health.lifecycle_stage}
                </strong>
                {health.is_terminal ? " — closed, no cadence." : "."}
              </>
            ) : (
              <span className="text-muted-foreground">
                No stage yet — nobody has triaged this contact, so they are on
                the 7-day floor.
              </span>
            )}
          </p>

          <SequenceSummary
            contactId={contact.id}
            health={health}
            today={today}
            enroll={enroll}
          />

          <Button
            asChild
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs text-muted-foreground underline hover:no-underline"
          >
            <Link to={lifecyclePath(contact.id)}>
              Open the lifecycle page — the plan, the dates and the controls
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

const Headline = ({ state }: { state: ReturnType<typeof headline> }) => {
  switch (state.kind) {
    case "terminal":
      return <>Closed — no cadence</>;
    case "paused":
      return <>Paused until {formatDay(state.until)}</>;
    case "overdue":
      return (
        <span className="text-destructive">
          Overdue since{" "}
          {state.dueAt ? <RelativeDate date={state.dueAt} /> : "— no clock"}
        </span>
      );
    case "due":
      return (
        <>Next due {state.dueAt ? <RelativeDate date={state.dueAt} /> : "—"}</>
      );
    default:
      // Not "fine". The read failed or this contact is not in the view.
      return <>Follow-up state unknown</>;
  }
};

/**
 * Enough steps for any cadence anyone writes by hand, in one read.
 *
 * The same trade SequenceList makes and for the same reason: at a handful of
 * sequences with a few steps each, one read of the table is cheaper than a
 * per-sequence count query, and every number shown is then computed from rows
 * we hold rather than from a count that could disagree with them.
 */
const ALL_STEPS = 1000;

/**
 * Is a cadence running on this person, and if not, start one.
 *
 * WHY IT READS EVERY SEQUENCE AND NOT ONLY THE ACTIVE ONES. The picker offers
 * active sequences only, but the name of the one this contact is ALREADY on
 * has to be printable even after that sequence is retired -- and retiring is
 * how a cadence is taken out of use, so it will happen. Filtering the read
 * would turn the answer into `sequence #4`.
 *
 * THERE IS NO PROGRESS COUNTER HERE ANY MORE. "0 of 5 steps done, and step 1
 * is next" was true, useless, and the reason this panel got rewritten: it
 * describes a plan without showing one. What is left is the two facts a
 * record needs -- something is running, and this is when it next does
 * anything -- with the plan itself one link away.
 */
const SequenceSummary = ({
  contactId,
  health,
  today,
  enroll: enrollRpc,
}: {
  contactId: Identifier;
  health: ContactHealth | null;
  today: string;
  enroll: EnrollRpc;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const { data: enrollmentRows, error: enrollmentsError } =
    useGetList<Enrollment>("enrollments", {
      filter: { contact_id: contactId },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "created_at", order: "DESC" },
    });

  const { data: sequenceRows } = useGetList<Sequence>("sequences", {
    filter: {},
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const { data: stepRows } = useGetList<SequenceStep>("sequence_steps", {
    filter: {},
    pagination: { page: 1, perPage: ALL_STEPS },
    sort: { field: "step_no", order: "ASC" },
  });

  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const sequences = sequenceRows ?? [];
  const steps = stepRows ?? [];
  const stepsOf = (id: number) => steps.filter((s) => s.sequence_id === id);
  const nameOf = (id: number | null) =>
    id == null
      ? "a hand-built plan"
      : // Never a blank. A row for a sequence this read did not return still
        // has to say WHICH one, and the id is the only honest answer left.
        (sequences.find((s) => s.id === id)?.name ?? `sequence #${id}`);

  const active = activeEnrollment(enrollmentRows);

  /*
   * THIS CONTACT'S OWN PLAN. `enabled` because there is nothing to filter by
   * without an active enrollment: `enrollment_id: undefined` is not a
   * narrower query, it is an unfiltered read of every step in the database.
   */
  const { data: planRows } = useGetList<EnrollmentStep>(
    "enrollment_steps",
    {
      filter: { enrollment_id: active?.id },
      pagination: { page: 1, perPage: ALL_STEPS },
      sort: { field: "step_no", order: "ASC" },
    },
    { enabled: active != null },
  );

  const offerable = enrollableSequences(sequences);
  const chosenSequence = sequences.find((s) => String(s.id) === chosen) ?? null;

  /*
   * ONE CHECK DRIVES BOTH THE BUTTON AND THE SENTENCE. Disabling without
   * saying why is how a screen becomes something a person clicks at, and
   * saying why without disabling is an invitation to write the dead row.
   * Computed BEFORE the enrolled branch below, because that branch needs its
   * refusal too -- it is the reason there is no second picker.
   */
  const check = canEnroll(
    health,
    active,
    chosenSequence,
    chosenSequence ? stepsOf(chosenSequence.id) : [],
  );

  const enroll = async () => {
    setBusy(true);
    try {
      /*
       * enroll_contact(), NOT the data provider. The enrollment and its whole
       * plan are one transaction: an enrollment written without its steps has
       * no pending step, so the nightly sweep marks it completed and this
       * contact is silently never nurtured. See the header of ./enrollment.ts.
       */
      await startEnrollment(enrollRpc, contactId, {
        health,
        activeEnrollment: active,
        sequence: chosenSequence,
        steps: chosenSequence ? stepsOf(chosenSequence.id) : [],
      });
      await invalidateLifecycle(queryClient);
      setChosen(null);
      notify(`Enrolled in ${chosenSequence?.name}.`, { type: "success" });
    } catch (e) {
      notify((e as Error).message ?? "Could not enroll this contact", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  if (enrollmentsError)
    return (
      <p className="text-sm text-destructive">
        Could not read this contact&apos;s enrollments, so this is not a record
        of them being in no sequence — it is a record of not knowing.
      </p>
    );

  if (active) {
    const p = progress(planRows ?? []);
    const held = suspension(health, today);
    return (
      <div className="space-y-1 text-sm">
        <p>
          In <strong>{nameOf(active.sequence_id)}</strong>
          {/*
            crm#18. `paused` holds the contact's live slot -- that is what
            enrollments_one_active covers -- so this row IS the live
            enrollment and must not be summarized as one that is running.
            advance_enrollments() reads `e.status = 'active'`, so "next step
            due Tuesday" would be a date nothing acts on.
          */}
          {active.status === "paused" ? (
            <>
              {" "}
              — <strong>paused</strong>. No task is created from this plan while
              it is, and their own follow-up clock keeps running.
            </>
          ) : planRows === undefined ? (
            <> — reading the plan…</>
          ) : p.next ? (
            <>
              {" "}
              — next step due <RelativeDate date={p.next.due_at} />.
            </>
          ) : (
            <>
              {" "}
              — nothing pending, so the next nightly pass marks it completed.
            </>
          )}
        </p>
        {/*
          WHY THERE IS NO SECOND PICKER, said rather than implied. The absence
          of a control explains nothing, and enrollments_one_active is a
          Postgres constraint a person would otherwise meet as a violation.
          canEnroll() is the one check behind both, so this sentence and the
          missing picker can never disagree.
        */}
        {check.ok === false && check.refusal.kind === "already" && (
          <p className="text-xs text-muted-foreground">
            {check.refusal.reason}
          </p>
        )}
        {/*
          A live pause or a future commitment makes advance_enrollments() skip
          the enrollment entirely. The row still says `active`, so a panel
          that printed the status column would explain nothing at all about
          why no tasks are appearing.
        */}
        {held.suspended && (
          <p className="text-xs text-muted-foreground">
            Suspended — no task is being created while that holds, and the step
            is not skipped: it falls due the moment the hold lifts.
          </p>
        )}
      </div>
    );
  }

  // No picker at all on a closed record. Offering one would be offering a
  // click whose only effect is a row tonight's run deletes.
  if (check.ok === false && check.refusal.kind === "closed")
    return <p className="text-sm text-muted-foreground">{check.refusal.reason}</p>; // prettier-ignore

  if (offerable.length === 0)
    return (
      <p className="text-sm text-muted-foreground">
        {sequences.length === 0
          ? "No sequences exist yet, so there is nobody to enroll and nothing to enroll them in."
          : "Every sequence is retired, and retiring is how a cadence is taken out of use."}{" "}
        <Link
          to={MARKETING_PATHS.sequences}
          className="underline underline-offset-2"
        >
          Write a sequence
        </Link>
        .
      </p>
    );

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="enrollment-sequence">Enroll in</Label>
          <Select
            value={chosen ?? undefined}
            onValueChange={setChosen}
            disabled={busy}
          >
            <SelectTrigger id="enrollment-sequence" className="w-64">
              <SelectValue placeholder="Choose a sequence" />
            </SelectTrigger>
            <SelectContent>
              {offerable.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name} · {trackLabel(s.track)}
                  {/*
                    Labeled rather than hidden. A stepless sequence is the
                    DEFAULT state of a new one, so removing them from the list
                    would turn "you cannot enroll anyone yet" into an empty
                    picker that reads as broken.
                  */}
                  {stepsOf(s.id).length === 0 ? " — no steps" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={enroll}
          disabled={busy || !check.ok}
        >
          Enroll
        </Button>
      </div>

      {check.ok ? (
        <p className="text-xs text-muted-foreground">
          Step 1 falls due{" "}
          {check.firstStep.offset_days === 0
            ? "straight away"
            : `in ${check.firstStep.offset_days} days`}
          , and the nightly run turns it into a task. Nothing enrolls anybody
          automatically.
        </p>
      ) : check.refusal.kind === "no-sequence" ? (
        <p className="text-xs text-muted-foreground">{check.refusal.reason}</p>
      ) : (
        <p className="text-xs text-destructive">
          {check.refusal.reason}{" "}
          {/*
            THE BLOCKER LINKS TO WHERE IT IS LIFTED. "This sequence has no
            steps" with no way to reach the sequence is a dead end on the one
            path every first-time user takes.
          */}
          {check.refusal.sequence && (
            <Link
              to={sequencePath(check.refusal.sequence.id)}
              className="underline underline-offset-2"
            >
              {check.refusal.kind === "retired"
                ? `Open ${check.refusal.sequence.name}`
                : `Add a step to ${check.refusal.sequence.name}`}
            </Link>
          )}
        </p>
      )}
    </div>
  );
};
